import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { marketingApi } from "../utils/api";

export function MarketingDashboard() {
  const [sources, setSources] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [visitorsList, setVisitorsList] = useState<any[]>([]);
  const [visitorsLoading, setVisitorsLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setVisitorsLoading(true);
    try {
      const [sourceRes, campaignRes, geoRes, visitorsRes] = await Promise.all([
        marketingApi.getAttributionReport({ from_date: fromDate, to_date: toDate, group_by: "source" }),
        marketingApi.getAttributionReport({ from_date: fromDate, to_date: toDate, group_by: "campaign" }),
        marketingApi.getAttributionReport({ from_date: fromDate, to_date: toDate, group_by: "geography" }),
        marketingApi.getVisitors({ from_date: fromDate, to_date: toDate, limit: 10 }),
      ]);

      const sourceData = sourceRes?.data?.groups || [];
      const campaignData = campaignRes?.data?.groups || [];
      const geoData = geoRes?.data?.groups || [];
      const visitorList = visitorsRes?.data?.visitors || [];
      const totalVisitors = visitorsRes?.data?.pagination?.total || 0;

      setSources(sourceData);
      setCampaigns(campaignData);

      // Extract unique states and cities from geoData
      const stateMap = new Map();
      const cityMap = new Map();
      geoData.forEach((g: any) => {
        if (g.state) {
          const s = stateMap.get(g.state) || { state: g.state, visitors: 0, orders: 0 };
          s.visitors += g.visitors || 0;
          s.orders += g.orders || 0;
          stateMap.set(g.state, s);
        }
        if (g.city) {
          const c = cityMap.get(g.city) || { city: g.city, visitors: 0, orders: 0 };
          c.visitors += g.visitors || 0;
          c.orders += g.orders || 0;
          cityMap.set(g.city, c);
        }
      });

      setStates(Array.from(stateMap.values()).sort((a: any, b: any) => b.visitors - a.visitors));
      setCities(Array.from(cityMap.values()).sort((a: any, b: any) => b.visitors - a.visitors));
      
      let attributedVisitors = 0;
      let signups = 0;
      let orders = 0;
      let qrRegistrations = 0;
      let qrActivations = 0;

      campaignData.forEach((c: any) => {
        attributedVisitors += c.visitors || 0;
        signups += c.signups || 0;
        orders += c.orders || 0;
        qrRegistrations += c.qr_registrations || 0;
        qrActivations += c.qr_activations || 0;
      });

      setMetrics({
        totalVisitors,
        attributedVisitors,
        signups,
        orders,
        qrRegistrations,
        qrActivations
      });

      setVisitorsList(visitorList);
    } catch (error) {
      console.error("Failed to fetch marketing report", error);
    } finally {
      setLoading(false);
      setVisitorsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [fromDate, toDate]);

  return (
    <div className="space-y-6 mt-4">
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div>
              <label className="block text-sm mb-2 text-foreground">From Date</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-gray-300 w-[180px]"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">To Date</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-gray-300 w-[180px]"
              />
            </div>
            <Button onClick={fetchReport} variant="outline" className="mb-[2px]">
              Apply Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-8">Loading analytics...</div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Visitors</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{metrics.totalVisitors || metrics.total_visitors || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Attributed Visitors</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{metrics.attributedVisitors || metrics.attributed_visitors || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Signups</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{metrics.signups || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Orders</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{metrics.orders || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">QR Registrations</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{metrics.qrRegistrations || metrics.qr_registrations || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">QR Activations</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{metrics.qrActivations || metrics.qr_activations || 0}</div></CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source Report */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Source Report</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Visitors</TableHead>
                      <TableHead>Signups</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>QR Acts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sources.map((s: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{s.source || s._id?.source || '(Direct)'}</TableCell>
                        <TableCell>{s.visitors || 0}</TableCell>
                        <TableCell>{s.signups || 0}</TableCell>
                        <TableCell>{s.orders || 0}</TableCell>
                        <TableCell>{s.qrActivations || s.qr_activations || 0}</TableCell>
                      </TableRow>
                    ))}
                    {sources.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center py-4">No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Campaign Report */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Campaign Report</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Visitors</TableHead>
                      <TableHead>Signups</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>QR Acts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaigns.map((c: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{c.campaign || c._id?.campaign || '(None)'}</TableCell>
                        <TableCell>{c.visitors || 0}</TableCell>
                        <TableCell>{c.signups || 0}</TableCell>
                        <TableCell>{c.orders || 0}</TableCell>
                        <TableCell>{c.qrActivations || c.qr_activations || 0}</TableCell>
                      </TableRow>
                    ))}
                    {campaigns.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center py-4">No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Geo Report (State) */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>By State</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>State</TableHead>
                      <TableHead>Visitors</TableHead>
                      <TableHead>Orders</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {states.map((s: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{s.state || '—'}</TableCell>
                        <TableCell>{s.visitors || 0}</TableCell>
                        <TableCell>{s.orders || 0}</TableCell>
                      </TableRow>
                    ))}
                    {states.length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center py-4">No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Geo Report (City) */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>By City</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>City</TableHead>
                      <TableHead>Visitors</TableHead>
                      <TableHead>Orders</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cities.map((c: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{c.city || '—'}</TableCell>
                        <TableCell>{c.visitors || 0}</TableCell>
                        <TableCell>{c.orders || 0}</TableCell>
                      </TableRow>
                    ))}
                    {cities.length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center py-4">No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
          
          {/* Recent Visitors Report */}
          <Card className="shadow-sm mt-6">
            <CardHeader>
              <CardTitle>Recent Visitors</CardTitle>
            </CardHeader>
            <CardContent>
              {visitorsLoading ? (
                <div className="py-4 text-center text-muted-foreground">Loading visitors...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Visitor ID / IP Hash</TableHead>
                      <TableHead>Source / Medium</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>User / Guest</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visitorsList.map((v: any, i: number) => {
                      const cty = v.location?.city || v.city;
                      const st = v.location?.state || v.state;
                      const ctry = v.location?.country || v.country;
                      const locationStr = [cty, st, ctry].filter(Boolean).join(", ");
                      const source = v.attribution?.latest_utm_source || v.attribution?.utm_source || v.source;
                      const medium = v.attribution?.latest_utm_medium || v.attribution?.utm_medium || v.medium;
                      const campaign = v.attribution?.latest_utm_campaign || v.attribution?.utm_campaign || v.campaign;
                      const date = v.latest_seen_at || v.first_seen_at || v.created_at;
                      return (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{v.visitor_id || '—'}</TableCell>
                          <TableCell>
                            {source ? `${source} / ${medium || '—'}` : '—'}
                          </TableCell>
                          <TableCell>{campaign || '—'}</TableCell>
                          <TableCell>{locationStr || '—'}</TableCell>
                          <TableCell>{v.user_id ? "Registered" : "Guest"}</TableCell>
                          <TableCell>{date ? new Date(date).toLocaleDateString() : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                    {visitorsList.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-4">No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
